import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const configuredBackendUrl = process.env.REACT_APP_BACKEND_URL?.replace(/\/$/, '');
const API_URL = configuredBackendUrl ? `${configuredBackendUrl}/api` : '/api';

// ---------------------------------------------------------------------------
// Token storage strategy:
//   Primary:  httpOnly 'access_token' cookie (XSS-safe, set by the backend)
//   Fallback: Authorization: Bearer header (API clients / mobile)
//
// The frontend no longer stores tokens in localStorage.  On page load we call
// GET /auth/me with withCredentials=true; if the cookie is valid the user is
// restored.  The React `user` state is the source of truth — token is kept
// only for components that still read `useAuth().token` as a truthy flag.
// ---------------------------------------------------------------------------

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  // `token` kept for backward-compat reading (truthy = authenticated).
  // NOT stored in localStorage — derived from the /auth/me response.
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const isRefreshing = useRef(false);
  const refreshSubscribers = useRef([]);

  const subscribeTokenRefresh = useCallback((callback) => {
    refreshSubscribers.current.push(callback);
  }, []);

  const onTokenRefreshed = useCallback(() => {
    refreshSubscribers.current.forEach((cb) => cb());
    refreshSubscribers.current = [];
  }, []);

  // Single axios instance — withCredentials sends httpOnly cookies on every request.
  const api = useMemo(() => axios.create({
    baseURL: API_URL,
    withCredentials: true,
  }), []);

  // Response interceptor — handle token refresh and gate redirects
  useEffect(() => {
    const interceptorId = api.interceptors.response.use(
      (response) => response,
      async (error) => {
      const originalRequest = error.config;

      // EMAIL_NOT_VERIFIED gate
      const detail = error.response?.data?.detail;
      const isEmailGate =
        error.response?.status === 403 &&
        ((typeof detail === 'object' && detail?.code === 'EMAIL_NOT_VERIFIED') ||
          (typeof detail === 'string' && detail.toLowerCase().includes('verify your email')));
      if (isEmailGate && typeof window !== 'undefined') {
        if (!window.location.pathname.startsWith('/verify-email')) {
          window.location.assign('/verify-email-required');
        }
      }

      // Soft-paywall upgrade modal
      const isUpgrade =
        error.response?.status === 403 &&
        typeof detail === 'string' &&
        (detail.toLowerCase().includes('upgrade') ||
          detail.toLowerCase().includes('plan does not include') ||
          detail.toLowerCase().includes('not available on your plan'));
      if (isUpgrade && !originalRequest?.skipUpgradeNotice && typeof window !== 'undefined') {
        let requiredPlan = 'professional';
        if (detail.toLowerCase().includes('global')) requiredPlan = 'global';
        if (detail.toLowerCase().includes('coach')) requiredPlan = 'coach';
        const url = (originalRequest?.url || '').replace(/^\/api\//, '').split('?')[0];
        window.dispatchEvent(new CustomEvent('upgrade-required', {
          detail: { requiredPlan, featureName: url || 'this feature' },
        }));
      }

      // 401 → try to refresh via httpOnly refresh_token cookie
      if (error.response?.status === 401 && !originalRequest._retry) {
        if (isRefreshing.current) {
          return new Promise((resolve) => {
            subscribeTokenRefresh(() => {
              resolve(api(originalRequest));
            });
          });
        }

        originalRequest._retry = true;
        isRefreshing.current = true;

        try {
          // Cookie auto-sent via withCredentials; no body needed
          await axios.post(`${API_URL}/auth/refresh`, {}, { withCredentials: true });
          onTokenRefreshed();
          isRefreshing.current = false;
          return api(originalRequest);
        } catch (refreshError) {
          isRefreshing.current = false;
          setToken(null);
          setUser(null);
          return Promise.reject(refreshError);
        }
      }

        return Promise.reject(error);
      }
    );

    return () => api.interceptors.response.eject(interceptorId);
  }, [api, onTokenRefreshed, subscribeTokenRefresh]);

  // On mount: restore session from httpOnly cookie via /auth/me
  const fetchUser = useCallback(async () => {
    try {
      const response = await api.get('/auth/me');
      setUser(response.data);
      setToken('cookie-session'); // truthy flag — not the actual token value
    } catch (error) {
      // 401 = no valid cookie; not an error — user just isn't logged in
      setToken(null);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  const login = async (email, password) => {
    const response = await api.post('/auth/login', { email, password });
    if (response.data?.mfa_required) {
      return {
        mfa_required: true,
        mfa_token: response.data.mfa_token,
        backup_codes_remaining: response.data.backup_codes_remaining,
      };
    }
    const { user: userData } = response.data;
    setToken('cookie-session');
    setUser(userData);
    return userData;
  };

  const verify2fa = async (mfa_token, code) => {
    const response = await api.post('/auth/2fa/verify', { mfa_token, code });
    const { user: userData } = response.data;
    setToken('cookie-session');
    setUser(userData);
    return {
      ...userData,
      method_used: response.data.method_used,
      backup_codes_remaining: response.data.backup_codes_remaining,
    };
  };

  const register = async (email, password, full_name, referral_code = null) => {
    const payload = { email, password, full_name };
    if (referral_code) payload.referral_code = referral_code;
    const response = await api.post('/auth/register', payload);
    const { user: userData } = response.data;
    setToken('cookie-session');
    setUser(userData);
    return userData;
  };

  const verifyOtpSignup = async (email, otp) => {
    const response = await api.post('/auth/verify-signup-otp', { email, otp });
    const { user: userData } = response.data;
    setToken('cookie-session');
    setUser(userData);
    return userData;
  };

  const logout = async ({ requireServer = false } = {}) => {
    try {
      // Tell the backend to expire the httpOnly cookies
      await api.post('/auth/logout');
    } catch (_) {
      if (requireServer) throw _;
      // Best-effort — clear state regardless
    }
    setToken(null);
    setUser(null);
    try { window.google?.accounts?.id?.disableAutoSelect(); } catch { /* optional Google SDK */ }
  };

  const loginWithGoogle = async (idToken) => {
    const response = await api.post('/auth/google/session', { id_token: idToken });
    const { user: userData } = response.data;
    setToken('cookie-session');
    setUser(userData);
    return { user: userData, is_new_user: Boolean(response.data?.is_new_user) };
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, login, register, verifyOtpSignup, verify2fa, logout, loginWithGoogle, api }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

